import { useEffect, useRef, useCallback } from 'react';
import { api } from '../api/axios';

interface SessionManagerOptions {
  onSessionExpiring?: (timeRemaining: number) => void;
  onSessionExpired?: () => void;
  warningThreshold?: number; // Minutes before expiration to show warning
  refreshThreshold?: number; // Minutes before expiration to auto-refresh
}

export const useSessionManager = (options: SessionManagerOptions = {}) => {
  const {
    onSessionExpiring,
    onSessionExpired,
    warningThreshold = 5, // Warn 5 minutes before expiration
    refreshThreshold = 10, // Auto-refresh 10 minutes before expiration
  } = options;

  const sessionExpiresAtRef = useRef<Date | null>(null);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const checkTimerRef = useRef<NodeJS.Timeout | null>(null);

  const clearTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    if (checkTimerRef.current) {
      clearTimeout(checkTimerRef.current);
      checkTimerRef.current = null;
    }
  }, []);

  const refreshSession = useCallback(async (): Promise<boolean> => {
    try {
      const res = await api.post('/api/session/refresh');
      if (res.data.success && res.data.session_expires_at) {
        sessionExpiresAtRef.current = new Date(res.data.session_expires_at);
        setupTimers();
        return true;
      }
      return false;
    } catch (error: any) {
      if (error?.response?.status === 401) {
        // Session expired
        clearTimers();
        onSessionExpired?.();
        return false;
      }
      return false;
    }
  }, [onSessionExpired, clearTimers]);

  const setupTimers = useCallback(() => {
    clearTimers();

    if (!sessionExpiresAtRef.current) return;

    const now = new Date();
    const expiresAt = sessionExpiresAtRef.current;
    const totalMsUntilExpiration = expiresAt.getTime() - now.getTime();
    const totalMinutesUntilExpiration = totalMsUntilExpiration / 1000 / 60;

    if (totalMinutesUntilExpiration <= 0) {
      // Already expired
      onSessionExpired?.();
      return;
    }

    // Set warning timer (warn X minutes before expiration)
    const warningMs = Math.max(0, totalMsUntilExpiration - warningThreshold * 60 * 1000);
    if (warningMs > 0 && onSessionExpiring) {
      warningTimerRef.current = setTimeout(() => {
        const remainingMinutes = Math.ceil(
          (expiresAt.getTime() - new Date().getTime()) / 1000 / 60
        );
        onSessionExpiring(remainingMinutes);
      }, warningMs);
    }

    // Set auto-refresh timer (refresh X minutes before expiration)
    const refreshMs = Math.max(0, totalMsUntilExpiration - refreshThreshold * 60 * 1000);
    if (refreshMs > 0) {
      refreshTimerRef.current = setTimeout(() => {
        refreshSession();
      }, refreshMs);
    }

    // Check session validity periodically (every minute)
    checkTimerRef.current = setTimeout(() => {
      if (sessionExpiresAtRef.current) {
        const now = new Date();
        if (now >= sessionExpiresAtRef.current) {
          // Session expired
          clearTimers();
          onSessionExpired?.();
        } else {
          // Continue checking
          setupTimers();
        }
      }
    }, 60000); // Check every minute
  }, [warningThreshold, refreshThreshold, onSessionExpiring, onSessionExpired, refreshSession, clearTimers]);

  const updateSessionExpiry = useCallback((expiresAt: string | Date) => {
    sessionExpiresAtRef.current = typeof expiresAt === 'string' ? new Date(expiresAt) : expiresAt;
    setupTimers();
  }, [setupTimers]);

  const clearSession = useCallback(() => {
    sessionExpiresAtRef.current = null;
    clearTimers();
  }, [clearTimers]);

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  return {
    updateSessionExpiry,
    refreshSession,
    clearSession,
    sessionExpiresAt: sessionExpiresAtRef.current,
  };
};

